import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { FileText, Loader2, Pencil, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AdjustNextNumberDialog } from "@/components/resolutions/AdjustNextNumberDialog";
import { CapacityWarning } from "@/components/resolutions/CapacityWarning";
import { ResolutionCard } from "@/components/resolutions/ResolutionCard";
import { ResolutionFormDialog } from "@/components/resolutions/ResolutionFormDialog";
import { SyncResolutionsButton } from "@/components/resolutions/SyncResolutionsButton";
import { useResolutions } from "@/lib/api/resolutions.queries";
import { useActiveCompany } from "@/lib/company-context";
import {
  DIAN_DOCUMENT_KINDS,
  type DianDocumentKind,
  type ResolutionWithStats,
} from "@/lib/api/resolutions";

export const Route = createFileRoute("/companies/$companyId/resoluciones/")({
  component: ResolutionsIndexPage,
});

function ResolutionsIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <ResolutionsIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function ResolutionsIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const query = useResolutions(companyId, { status: "all" });

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ResolutionWithStats | null>(null);
  const [adjusting, setAdjusting] = useState<ResolutionWithStats | null>(null);

  // The API lists only the deployment's DIAN environment's ranges.
  const resolutions = query.data?.resolutions ?? [];

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {t("resolutions.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("resolutions.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("resolutions.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SyncResolutionsButton companyId={companyId} />
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t("resolutions.add")}
          </Button>
        </div>
      </div>

      <CapacityWarning resolutions={resolutions} />

      {query.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : query.isError ? (
        <ErrorState
          title={t("resolutions.error.load_title")}
          description={t("resolutions.error.load_body")}
          onRetry={() => void query.refetch()}
          retryLabel={t("common.retry")}
          error={query.error}
        />
      ) : resolutions.length === 0 ? (
        <EmptyState onAdd={() => setCreateOpen(true)} />
      ) : (
        <ResolutionsByDocKind
          resolutions={resolutions}
          onEdit={setEditing}
          onAdjust={setAdjusting}
        />
      )}

      <ResolutionFormDialog
        companyId={companyId}
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode={{ kind: "create" }}
      />
      {editing ? (
        <ResolutionFormDialog
          companyId={companyId}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          mode={{ kind: "edit", resolution: editing }}
        />
      ) : null}
      {adjusting ? (
        <AdjustNextNumberDialog
          companyId={companyId}
          resolution={adjusting}
          open
          onOpenChange={(open) => {
            if (!open) setAdjusting(null);
          }}
        />
      ) : null}
    </div>
  );
}

function ResolutionsByDocKind({
  resolutions,
  onEdit,
  onAdjust,
}: {
  resolutions: ResolutionWithStats[];
  onEdit: (r: ResolutionWithStats) => void;
  onAdjust: (r: ResolutionWithStats) => void;
}) {
  const { t } = useTranslation();

  if (resolutions.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          {t("resolutions.empty.env")}
        </CardContent>
      </Card>
    );
  }

  // Group by document_kind, ordered by the canonical DIAN_DOCUMENT_KINDS list
  // so the page reads the same way across companies.
  const grouped = new Map<DianDocumentKind, ResolutionWithStats[]>();
  for (const k of DIAN_DOCUMENT_KINDS) grouped.set(k, []);
  for (const r of resolutions) {
    grouped.get(r.documentKind)?.push(r);
  }

  return (
    <div className="flex flex-col gap-6">
      {DIAN_DOCUMENT_KINDS.map((kind) => {
        const items = grouped.get(kind) ?? [];
        if (items.length === 0) return null;
        return (
          <section key={kind} className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              {t(`resolutions.documentKind.${kind}`)}
            </h2>
            <div className="grid gap-4 md:grid-cols-2">
              {items.map((r) => (
                <div key={r.id} className="relative">
                  <ResolutionCard resolution={r} onAdjustNextNumber={onAdjust} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-2 h-7 px-2 text-xs"
                    onClick={() => onEdit(r)}
                  >
                    <Pencil className="h-3 w-3" />
                    {t("common.edit")}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <FileText className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">{t("resolutions.empty.title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("resolutions.empty.body")}
          </p>
        </div>
        <Button onClick={onAdd}>
          <Plus className="h-4 w-4" />
          {t("resolutions.add")}
        </Button>
      </CardContent>
    </Card>
  );
}
